import { ICharacter } from "@next-platform/client-common-core";
import {
  Avatar,
  AvatarShape,
  AvatarSize,
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconVariants,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";

import Trans from "@/components/Trans/Trans";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

interface IGroupChatCharacterSelectorProps {
  autoResponse: boolean;
  hasPendingMessage: boolean;
  sub_characters: ICharacter[];
  isDisabled?: boolean;
  handleCharacterClick: (characterId: null | string) => void;
  isGroupChatAutoResponseAllowed: boolean;
  isGroupChatAutoResponsePaywall: boolean;
}

const GroupChatCharacterSelector = ({
  sub_characters,
  isDisabled,
  handleCharacterClick,
  isGroupChatAutoResponseAllowed,
  isGroupChatAutoResponsePaywall,
  autoResponse,
  hasPendingMessage,
}: IGroupChatCharacterSelectorProps) => {
  const isPartner = getIsPartner();
  return (
    <div className="w-full">
      <div className="mx-auto max-w-[800px] px-2 py-2">
        <div className="mb-4">
          <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-11">
            {!autoResponse && hasPendingMessage ? (
              <Trans i18nKey="chat:page.groupChat.selector.pendingMessage" />
            ) : (
              <Trans i18nKey="chat:page.groupChat.selector.keepChatRolling" />
            )}
          </Text>
        </div>

        <div className="flex flex-wrap gap-2 overflow-auto max-h-[92px]">
          {!isPartner && (
            <Button
              disabled={isDisabled}
              variant={ButtonVariant.OUTLINED}
              size={ButtonSize.MEDIUM}
              className={cn(
                "border-2",
                !isGroupChatAutoResponseAllowed && (isGroupChatAutoResponsePaywall ? "opacity-50" : "opacity-50 cursor-not-allowed"),
              )}
              onClick={() => handleCharacterClick(null)}
            >
              <div className="flex items-center gap-2">
                <Icon icon={IconVariants.SKIP_FORWARD} />
                <span>
                  <Trans i18nKey="chat:page.groupChat.selector.next" />
                </span>
              </div>
            </Button>
          )}

          {sub_characters?.map((char: Partial<ICharacter>, index: number) => (
            <Button
              key={index}
              disabled={isDisabled}
              variant={ButtonVariant.OUTLINED}
              size={ButtonSize.MEDIUM}
              className="!justify-start shrink-0"
              onClick={() => handleCharacterClick(char?.id || "")}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                <Avatar
                  shape={AvatarShape.SQUARE}
                  src={getAvatarUrl(char?.avatar_url || "", { preset: AvatarPreset.AVATAR_48X48 }) || ""}
                  name={char?.name || ""}
                  size={AvatarSize.XSMALL}
                  imageProps={{ loading: "lazy" }}
                />
                <span className="truncate block max-w-[100px]" title={char?.name}>
                  {char?.name}
                </span>
              </div>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
export default GroupChatCharacterSelector;
